import { Record, List } from 'immutable';
import GatewayBatteryConstants from '~/constants/gateway-battery-constants';
import GatewayNetworkConstants from '~/constants/gateway-network-constants';
import GatewayStatusConstants from '~/constants/gateway-status-constants';
import GatewayTypeConstants from '~/constants/gateway-type-constants';
import StatusTimeRecord from './StatusTimeRecord';

const keys: Fields = {
  isDeleting: false,
  id: '',
  name: '',
  type: GatewayTypeConstants.UNDEFINED,
  status: GatewayStatusConstants.UNDEFINED,
  fixedPosition: false,
  locationName: '',
  connected: false,
  version: '',
  updateRequired: false,
  bleOn: false,
  lastConnectedStatus: new StatusTimeRecord(),
  lastDeviceConnectedStatus: new StatusTimeRecord(),
  messages: List(),
  locationServicesOn: false,
  networkConnectionStatus: GatewayNetworkConstants.UNDEFINED,
  husqiotConnected: false,
  pluggedIn: false,
  batteryStatus: GatewayBatteryConstants.UNDEFINED,
  noConnectedDeviceSinceStartUp: false,
  numberOfConnectedDevices: 0,
  numberOfDevicesWaitingToConnect: 0,
  numberOfLastSeenDevices: 0,
  timeInMinutesSinceLastConnectedDevices: 0,
  hardwareManufacturer: '',
  hardwareModel: '',
  operatingSystemVersion: '',
};

export interface Fields {
  isDeleting: boolean;
  id: string;
  name: string;
  type: GatewayTypeConstants;
  status: GatewayStatusConstants;
  fixedPosition: boolean;
  locationName: string;
  connected: boolean;
  version: string;
  updateRequired: boolean;
  bleOn: boolean;
  lastConnectedStatus: StatusTimeRecord;
  lastDeviceConnectedStatus: StatusTimeRecord;
  messages: List<string>;
  locationServicesOn: boolean;
  networkConnectionStatus: GatewayNetworkConstants;
  husqiotConnected: boolean;
  pluggedIn: boolean;
  batteryStatus: GatewayBatteryConstants;
  noConnectedDeviceSinceStartUp: boolean;
  numberOfConnectedDevices: number;
  numberOfDevicesWaitingToConnect: number;
  numberOfLastSeenDevices: number;
  timeInMinutesSinceLastConnectedDevices: number;
  hardwareManufacturer: string;
  hardwareModel: string;
  operatingSystemVersion: string;
}

export default class GatewayRecord extends Record<Fields>(keys) {
  constructor(values?) {
    super(values);

    const newValues: Partial<Fields> = {};
    if (values) {
      if (values.lastConnectedStatus) {
        newValues.lastConnectedStatus = new StatusTimeRecord(values.lastConnectedStatus);
      }

      if (values.lastDeviceConnectedStatus) {
        newValues.lastDeviceConnectedStatus = new StatusTimeRecord(
          values.lastDeviceConnectedStatus
        );
      }
    }

    return this.merge(newValues);
  }

  getName() {
    return this.name;
  }

  getStatus() {
    return this.status;
  }

  getNumberOfLastSeenDevices() {
    return this.numberOfLastSeenDevices;
  }

  getNumberOfDevicesWaitingToConnect() {
    return this.numberOfDevicesWaitingToConnect;
  }

  getHardwareString() {
    return `${this.hardwareManufacturer} ${this.hardwareModel}`;
  }

  getLastConnectedStatus() {
    return this.lastConnectedStatus;
  }

  getLastDeviceConnectedStatus() {
    return this.lastDeviceConnectedStatus;
  }

  getGatewayVersion() {
    return this.version;
  }

  getGatewayType() {
    return this.type;
  }

  getOperatingSystem() {
    return this.operatingSystemVersion;
  }

  getNetworkConnectionStatus() {
    return this.networkConnectionStatus;
  }

  getLocationName() {
    return this.locationName;
  }

  getMessages() {
    return this.messages;
  }

  isBleOn() {
    return this.bleOn;
  }

  isLocationServicesOn() {
    return this.locationServicesOn;
  }

  isFixedPosition() {
    return this.fixedPosition;
  }

  isPluggedIn() {
    return this.pluggedIn;
  }

  getBatteryStatus() {
    return this.batteryStatus;
  }

  isConnected() {
    return this.connected;
  }

  isHusqiotConnected() {
    return this.husqiotConnected;
  }

  isNoConnectedDeviceSinceStartUp() {
    return this.noConnectedDeviceSinceStartUp;
  }
}
